import { z } from "zod";
import { AlignmentType, BorderStyle, Document, HeadingLevel, ImageRun, LevelFormat, Packer, PageBreak, Paragraph, ShadingType, Table, TableCell, TableRow, TextRun, VerticalAlignTable, WidthType } from "docx";
//#region src/geometry/units.ts
/**
* Unit conversions.
*
* **Points are the canonical unit of this codebase.** Page sizes, margins,
* print and PDF all speak points (1pt = 1/72in), so the layout engine works
* exclusively in points and converts only at the rendering edge. CSS pixels
* are a display concern; they never enter the layout model.
*/
const PT_PER_INCH = 72;
const CSS_PX_PER_INCH = 96;
const MM_PER_INCH = 25.4;
function inchToPt(inches) {
	return inches * 72;
}
function ptToInch(pt) {
	return pt / 72;
}
function mmToPt(mm) {
	return mm / MM_PER_INCH * 72;
}
function ptToMm(pt) {
	return pt / 72 * MM_PER_INCH;
}
/** Points → CSS pixels at 100% zoom. Zoom is applied as a transform, never here. */
function ptToCssPx(pt) {
	return pt / 72 * 96;
}
function cssPxToPt(px) {
	return px / 96 * 72;
}
/**
* Round to a fixed number of decimal places.
*
* Repeated add/subtract of converted values accumulates float error, and in a
* layout engine that error shows up as a stray one-pixel page break. Rounding
* at well-defined points keeps comparisons stable and, more importantly, keeps
* pagination reproducible between runs.
*/
function roundPt(pt, decimals = 3) {
	const factor = 10 ** decimals;
	return Math.round(pt * factor) / factor;
}
//#endregion
//#region src/geometry/page-size.ts
const fromMm = (label, w, h) => ({
	label,
	widthPt: roundPt(mmToPt(w), 2),
	heightPt: roundPt(mmToPt(h), 2)
});
const fromInch = (label, w, h) => ({
	label,
	widthPt: roundPt(inchToPt(w), 2),
	heightPt: roundPt(inchToPt(h), 2)
});
/**
* The identifiers, as a tuple.
*
* Declared before the size table so `PAGE_SIZES` can be typed as a total
* record over it: adding an id here without adding its dimensions is a
* compile error, and validators can consume the tuple directly instead of
* re-listing the names.
*/
const PAGE_SIZE_IDS = [
	"a3",
	"a4",
	"a5",
	"letter",
	"legal",
	"tabloid"
];
/**
* Built-in page sizes, always stored portrait — `resolvePageSize` applies
* orientation. Derived from their defining mm/inch dimensions rather than
* hardcoded points, so the numbers are self-evidently correct.
*/
const PAGE_SIZES = {
	a3: fromMm("A3", 297, 420),
	a4: fromMm("A4", 210, 297),
	a5: fromMm("A5", 148, 210),
	letter: fromInch("Letter", 8.5, 11),
	legal: fromInch("Legal", 8.5, 14),
	tabloid: fromInch("Tabloid", 11, 17)
};
function isPageSizeId(value) {
	return Object.hasOwn(PAGE_SIZES, value);
}
/** Apply orientation to a portrait-defined size. */
function orient(size, orientation) {
	return orientation === "landscape" ? {
		widthPt: size.heightPt,
		heightPt: size.widthPt
	} : {
		widthPt: size.widthPt,
		heightPt: size.heightPt
	};
}
function resolvePageSize(id, orientation) {
	return orient(PAGE_SIZES[id], orientation);
}
//#endregion
//#region src/schema/cell-properties.ts
const CELL_VERTICAL_ALIGNS = [
	"top",
	"middle",
	"bottom"
];
/** A cell with nothing set: everything inherits from the table's own styling. */
const EMPTY_CELL_PROPERTIES = {
	backgroundColor: null,
	verticalAlign: null,
	borderColor: null,
	borderWidthPx: null,
	paddingPx: null
};
/** The attribute names carried on a cell node, for the extension to declare. */
const CELL_PROPERTY_ATTRIBUTES = [
	"backgroundColor",
	"verticalAlign",
	"borderColor",
	"borderWidthPx",
	"paddingPx"
];
/** Nobody needs a 40px border, and one would paint over the next cell. */
const MAX_BORDER_WIDTH_PX = 12;
/** Padding beyond this leaves no room for text in a normal column. */
const MAX_PADDING_PX = 48;
/**
* Colours that are safe to put in a `style` attribute.
*
* These values are written into exported HTML, which is a file someone will
* open in a browser, so the check is an allowlist rather than a denylist: hex,
* the `rgb()`/`rgba()` functions with numeric arguments, and bare names. A
* bare name cannot carry a scheme or a function call, so `red` is fine and
* `url(...)` never matches anything here.
*/
const HEX = /^#(?:[0-9a-f]{3,4}|[0-9a-f]{6}|[0-9a-f]{8})$/i;
const RGB_FUNCTION = /^rgba?\(\s*[\d.\s,%/]+\)$/i;
const BARE_NAME = /^[a-z]{3,20}$/i;
function isSafeCssColor(value) {
	if (typeof value !== "string") return false;
	const trimmed = value.trim();
	if (trimmed.length === 0 || trimmed.length > 32) return false;
	return HEX.test(trimmed) || RGB_FUNCTION.test(trimmed) || BARE_NAME.test(trimmed);
}
/**
* A colour as six-digit hex, or `null` when it cannot be expressed that way.
*
* Needed in two places that both went wrong without it. The DOM normalises
* `#eef2f7` to `rgb(238, 242, 247)` the moment a cell is written to HTML and
* parsed back, so a copied-and-pasted shaded cell reached the DOCX exporter as
* `rgb(...)`, failed its hex-only check, and exported unshaded — precisely the
* screen-versus-Word disagreement this module exists to prevent. And an
* `<input type="color">` given anything but hex silently shows `#000000`, so
* the swatch for a shaded cell read black and nudging it committed black.
*/
function toHexColor(value) {
	if (!isSafeCssColor(value)) return null;
	const trimmed = value.trim();
	if (HEX.test(trimmed)) {
		const digits = trimmed.slice(1);
		if (digits.length === 3 || digits.length === 4) return `#${[...digits.slice(0, 3)].map((digit) => digit + digit).join("")}`.toLowerCase();
		return `#${digits.slice(0, 6)}`.toLowerCase();
	}
	const rgb = trimmed.slice(trimmed.indexOf("(") + 1, trimmed.lastIndexOf(")")).split(/[\s,/]+/).filter((part) => part.length > 0).slice(0, 3).map((channel) => {
		const number = Number.parseFloat(channel);
		if (!Number.isFinite(number)) return NaN;
		return Math.round(channel.endsWith("%") ? number / 100 * 255 : number);
	});
	if (rgb.length < 3 || rgb.some((channel) => !Number.isFinite(channel))) return null;
	if (rgb.some((channel) => channel < 0 || channel > 255)) return null;
	return `#${rgb.map((channel) => channel.toString(16).padStart(2, "0")).join("")}`;
}
function readColor(value) {
	return isSafeCssColor(value) ? value.trim() : null;
}
function readLength(value, max) {
	const number = typeof value === "number" ? value : Number.parseFloat(String(value));
	if (!Number.isFinite(number) || number < 0) return null;
	return Math.min(Math.round(number), max);
}
function readVerticalAlign(value) {
	return CELL_VERTICAL_ALIGNS.find((align) => align === value) ?? null;
}
/**
* Read the properties off a cell's attributes.
*
* Everything unrecognised becomes `null` rather than being passed through.
* These attributes arrive from persisted documents and from pasted HTML, so
* this is a boundary, and a value that reaches an exporter unchecked reaches
* whatever opens the export.
*/
function readCellProperties(attrs) {
	if (typeof attrs !== "object" || attrs === null) return EMPTY_CELL_PROPERTIES;
	const record = attrs;
	return {
		backgroundColor: readColor(record["backgroundColor"]),
		verticalAlign: readVerticalAlign(record["verticalAlign"]),
		borderColor: readColor(record["borderColor"]),
		borderWidthPx: readLength(record["borderWidthPx"], 12),
		paddingPx: readLength(record["paddingPx"], 48)
	};
}
function hasCellProperties(properties) {
	return Object.values(properties).some((value) => value !== null);
}
/**
* The CSS declarations for a cell, in the order a stylesheet would write them.
*
* A border needs both a width and a colour to mean anything; a width alone
* would draw in whatever colour the table's own rule last set, which is not
* what the person choosing it asked for.
*/
function cellStyleDeclarations(properties) {
	const declarations = [];
	if (properties.backgroundColor) declarations.push(`background-color: ${properties.backgroundColor}`);
	if (properties.verticalAlign) declarations.push(`vertical-align: ${properties.verticalAlign}`);
	if (properties.paddingPx !== null) declarations.push(`padding: ${properties.paddingPx}px`);
	if (properties.borderWidthPx !== null || properties.borderColor) {
		const width = properties.borderWidthPx ?? 1;
		const color = properties.borderColor ?? "currentColor";
		declarations.push(width === 0 ? "border: none" : `border: ${width}px solid ${color}`);
	}
	return declarations;
}
/** The same, as a `style` attribute value. Empty when nothing is set. */
function cellStyle(properties) {
	return cellStyleDeclarations(properties).join("; ");
}
/**
* The colour an uncoloured highlight is.
*
* Defined once because four places need to agree: the editor's stylesheet,
* the toolbar's swatch, and both exporters. They did not — the stylesheet
* painted `#fff475` while the exporters had been given `#fff59d`, so every
* highlight made before colours existed rendered one yellow and exported
* another, and the menu showed the wrong swatch as pressed.
*
* The value is the stylesheet's, because that is what is already on screen in
* documents people have written.
*/
const DEFAULT_HIGHLIGHT_COLOR = "#fff475";
//#endregion
//#region src/geometry/page-background.ts
/**
* What is painted behind a page.
*
* Stored as **parts**, never as a CSS string. A string would have to be
* re-parsed by the print path and by every exporter, cannot be validated at
* the boundary, and is a straightforward injection route into the `style`
* attribute of an exported file. Colours go through the same check as table
* cell shading — one definition, because two would drift.
*
* None of this reaches the layout engine: a background changes no measurement,
* so it cannot change where a page breaks.
*/
const NO_PAGE_BACKGROUND = { kind: "none" };
/** The CSS `background` shorthand for a background, or `null` for none. */
function pageBackgroundCss(background, resolvedImageUrl) {
	switch (background.kind) {
		case "none": return null;
		case "color": return isSafeCssColor(background.color) ? background.color : null;
		case "gradient": {
			const stops = background.stops.filter((stop) => isSafeCssColor(stop.color)).map((stop) => `${stop.color} ${clampPercent(stop.positionPct)}%`);
			if (stops.length < 2) return null;
			return `linear-gradient(${Math.round(background.angleDeg)}deg, ${stops.join(", ")})`;
		}
		case "image": {
			if (!resolvedImageUrl) return null;
			const url = `url("${resolvedImageUrl.replace(/["\\]/g, "")}")`;
			if (background.fit === "tile") return `${url} repeat`;
			return `${url} center / ${background.fit} no-repeat`;
		}
	}
}
function clampPercent(value) {
	if (!Number.isFinite(value)) return 0;
	return Math.min(100, Math.max(0, Math.round(value)));
}
/** Is this background meant to appear on paper as well as on screen? */
function isPrinted(background) {
	return background.kind !== "none" && background.printed;
}
/**
* The colour a background paints with, if it paints a flat one.
*
* DOCX can express a page background colour and nothing else, so a gradient or
* an image exports as nothing rather than as a wrong approximation — the same
* rule the cell-shading mapping follows.
*/
function flatBackgroundColor(background) {
	return background.kind === "color" && isSafeCssColor(background.color) ? background.color : null;
}
/**
* Is a flat background dark enough that default body text will be hard to read?
*
* Used to *warn*, never to restyle. The document's colours belong to its
* author, and an editor that silently turns their text white the moment they
* pick a navy page has taken a decision that was not its to take.
*
* `null` when there is nothing to judge — a gradient or an image can be any
* shade anywhere, and guessing would cry wolf.
*/
function backgroundIsDark(background) {
	const hex = background.kind === "color" ? toHexColor(background.color) : null;
	if (!hex) return null;
	const channel = (offset) => Number.parseInt(hex.slice(offset, offset + 2), 16);
	return .2126 * channel(1) + .7152 * channel(3) + .0722 * channel(5) < 140;
}
//#endregion
//#region src/geometry/page-geometry.ts
/** Google Docs' default: one inch on every side. */
const DEFAULT_MARGINS = {
	topPt: inchToPt(1),
	rightPt: inchToPt(1),
	bottomPt: inchToPt(1),
	leftPt: inchToPt(1)
};
const DEFAULT_HEADER = {
	enabled: false,
	heightPt: inchToPt(.5)
};
const DEFAULT_FOOTER = {
	enabled: false,
	heightPt: inchToPt(.5)
};
const DEFAULT_PAGE_SETUP = {
	size: {
		kind: "preset",
		id: "a4"
	},
	orientation: "portrait",
	margins: DEFAULT_MARGINS,
	header: DEFAULT_HEADER,
	footer: DEFAULT_FOOTER,
	background: NO_PAGE_BACKGROUND,
	verticalAlign: "top"
};
var InvalidPageSetupError = class extends Error {
	constructor(message) {
		super(message);
		this.name = "InvalidPageSetupError";
	}
};
function resolveDimensions(setup) {
	if (setup.size.kind === "custom") {
		const { widthPt, heightPt } = setup.size;
		if (!(widthPt > 0) || !(heightPt > 0)) throw new InvalidPageSetupError(`Custom page size must be positive (got ${widthPt} × ${heightPt}pt)`);
		return orient({
			widthPt,
			heightPt
		}, setup.orientation);
	}
	return resolvePageSize(setup.size.id, setup.orientation);
}
function bandHeight(band) {
	return band.enabled ? band.heightPt : 0;
}
/**
* Resolve a `PageSetup` into the geometry the layout engine uses.
*
* Throws when the margins and header/footer bands leave no room for content.
* That is not a defensive nicety: a non-positive content height would make the
* pagination loop unable to place a single line, and it would emit pages
* forever. Failing loudly here keeps that impossible.
*/
function createPageGeometry(setup) {
	const { widthPt, heightPt } = resolveDimensions(setup);
	const { margins, header, footer } = setup;
	const contentWidthPt = roundPt(widthPt - margins.leftPt - margins.rightPt);
	const contentHeightPt = roundPt(heightPt - margins.topPt - margins.bottomPt - bandHeight(header) - bandHeight(footer));
	if (contentWidthPt <= 0) throw new InvalidPageSetupError(`Horizontal margins (${margins.leftPt} + ${margins.rightPt}pt) leave no content width on a ${widthPt}pt page`);
	if (contentHeightPt <= 0) throw new InvalidPageSetupError(`Vertical margins and header/footer bands leave no content height on a ${heightPt}pt page`);
	return {
		widthPt,
		heightPt,
		margins,
		header,
		footer,
		contentWidthPt,
		contentHeightPt,
		contentOriginPt: {
			xPt: margins.leftPt,
			yPt: roundPt(margins.topPt + bandHeight(header))
		}
	};
}
//#endregion
//#region src/geometry/ruler.ts
/** Distance between labelled ticks. */
const UNIT_LENGTH_PT = {
	in: 72,
	cm: mmToPt(10)
};
/** Unlabelled ticks per labelled one. */
const SUBDIVISIONS = {
	in: 8,
	cm: 2
};
/**
* What a drag snaps to.
*
* An eighth of an inch is fine enough to hit any sensible margin and coarse
* enough that a margin never ends up at 0.9821 inches — a number nobody chose
* and nobody can type back in.
*/
function snapIncrementPt(unit) {
	return UNIT_LENGTH_PT[unit] / SUBDIVISIONS[unit];
}
function snapToIncrement(valuePt, incrementPt) {
	if (!(incrementPt > 0)) return valuePt;
	return Math.round(valuePt / incrementPt) * incrementPt;
}
/**
* Smallest content column a drag may leave.
*
* Below this the page has no usable text area, and `createPageGeometry` would
* reject the setup outright — a drag should stop, not throw.
*/
const MIN_CONTENT_WIDTH_PT = 72;
/**
* Ticks across the whole page width.
*
* Numbered from the **content origin**, not the page edge: the numbers on a
* ruler answer "how far into my text am I", and a reader measuring an indent
* counts from where their text starts. Ticks still run through the margins, so
* the ruler reads as one continuous scale.
*/
function rulerTicks(geometry, unit = "in") {
	const unitPt = UNIT_LENGTH_PT[unit];
	const step = snapIncrementPt(unit);
	const origin = geometry.margins.leftPt;
	const contentEnd = geometry.widthPt - geometry.margins.rightPt;
	const ticks = [];
	const first = origin - Math.ceil(origin / step) * step;
	for (let position = first; position <= geometry.widthPt + .01; position += step) {
		const unitsFromOrigin = (position - origin) / unitPt;
		const major = Math.abs(unitsFromOrigin - Math.round(unitsFromOrigin)) < .001;
		const tick = {
			positionPt: Math.round(position * 100) / 100,
			major
		};
		const rounded = Math.round(unitsFromOrigin);
		if (major && rounded !== 0 && position >= origin && position <= contentEnd) tick.label = String(Math.abs(rounded));
		ticks.push(tick);
	}
	return ticks;
}
/**
* Clamp a dragged margin so the page keeps a usable content column.
*
* Clamping rather than rejecting: a drag that hits the limit should stop at
* it, not snap back or throw.
*/
function clampMargin({ pageWidthPt, oppositeMarginPt, proposedPt, minContentWidthPt = MIN_CONTENT_WIDTH_PT }) {
	const maximum = pageWidthPt - oppositeMarginPt - minContentWidthPt;
	return Math.max(0, Math.min(proposedPt, Math.max(0, maximum)));
}
/** Clamp a dragged paragraph indent to the content column. */
function clampIndent({ contentWidthPt, oppositeIndentPt, proposedPt, minWidthPt = 72 / 2 }) {
	const maximum = contentWidthPt - oppositeIndentPt - minWidthPt;
	return Math.max(0, Math.min(proposedPt, Math.max(0, maximum)));
}
//#endregion
//#region src/schema/node-types.ts
/**
* The document's vocabulary: every node and mark type a Burfy Docs document
* may contain.
*
* This registry is plain data on purpose. Three consumers read it and they
* must never disagree:
*
*   1. the editor, which registers a matching TipTap extension per entry;
*   2. the validator, which rejects a persisted document containing anything
*      not listed here;
*   3. the exporters, which walk the tree without importing ProseMirror.
*
* Adding a feature therefore starts here, and anything that forgets to update
* one of the three fails loudly rather than silently dropping content.
*/
const NODE_TYPES = {
	doc: "doc",
	paragraph: "paragraph",
	text: "text",
	heading: "heading",
	blockquote: "blockquote",
	codeBlock: "codeBlock",
	horizontalRule: "horizontalRule",
	/** An explicit, user-inserted page break — real content, unlike the
	*  automatic breaks, which are decorations derived from measurement. */
	pageBreak: "pageBreak",
	hardBreak: "hardBreak",
	bulletList: "bulletList",
	orderedList: "orderedList",
	listItem: "listItem",
	taskList: "taskList",
	taskItem: "taskItem",
	table: "table",
	tableRow: "tableRow",
	tableCell: "tableCell",
	tableHeader: "tableHeader",
	image: "image"
};
/**
* Marks.
*
* `textStyle` is the carrier for the continuous typographic attributes —
* font family, font size, colour, background colour, line height. TipTap
* models them as attributes of one mark rather than as separate marks, and
* the exporters rely on that shape.
*/
const MARK_TYPES = {
	bold: "bold",
	italic: "italic",
	underline: "underline",
	strike: "strike",
	code: "code",
	link: "link",
	highlight: "highlight",
	subscript: "subscript",
	superscript: "superscript",
	textStyle: "textStyle",
	/**
	* Records the roman text a transliterated word came from, so the candidate
	* list can be re-opened on it later. Editorial metadata, not formatting:
	* exporters drop it.
	*/
	translitSource: "translitSource"
};
const NODE_TYPE_SET = new Set(Object.values(NODE_TYPES));
const MARK_TYPE_SET = new Set(Object.values(MARK_TYPES));
function isKnownNodeType(name) {
	return NODE_TYPE_SET.has(name);
}
function isKnownMarkType(name) {
	return MARK_TYPE_SET.has(name);
}
/** Text alignment, shared by the schema, the toolbar and the exporters. */
const TEXT_ALIGNMENTS = [
	"left",
	"center",
	"right",
	"justify"
];
const HEADING_LEVELS = [
	1,
	2,
	3,
	4,
	5,
	6
];
/**
* Attributes carried by block nodes (paragraphs and headings).
*
* These are paragraph properties in the word-processor sense — the things
* Word and DOCX model per-paragraph rather than per-character. Keeping the
* names here means the editor, the layout engine and the DOCX mapper all
* read the same keys off a node.
*/
const BLOCK_ATTRS = {
	textAlign: "textAlign",
	/** Multiple of the font size, like CSS `line-height: <number>`. */
	lineHeight: "lineHeight",
	spaceBeforePt: "spaceBeforePt",
	spaceAfterPt: "spaceAfterPt",
	/** Left indent. */
	indentPt: "indentPt",
	/** Right indent — how far the text stops short of the right margin. */
	indentRightPt: "indentRightPt",
	/**
	* Extra indent applied to the first line only. Negative values produce a
	* hanging indent, which is what numbered and bibliographic layouts use.
	*/
	firstLineIndentPt: "firstLineIndentPt"
};
/** One indent step — half an inch, matching the default tab stop. */
const INDENT_STEP_PT = 36;
/** Ten steps in. Beyond this the content column stops being usable. */
const MAX_INDENT_PT = 360;
/**
* How an image sits in the flow.
*
* `wrapLeft` / `wrapRight` float it and let the paragraph's text run beside
* it; `break` gives it a line of its own; `inline` treats it as a very large
* character.
*/
const IMAGE_LAYOUTS = [
	"inline",
	"wrapLeft",
	"wrapRight",
	"break"
];
function isImageLayout(value) {
	return typeof value === "string" && IMAGE_LAYOUTS.includes(value);
}
/** Line-spacing presets offered in the toolbar. */
const LINE_HEIGHT_PRESETS = [
	1,
	1.15,
	1.5,
	2,
	2.5,
	3
];
//#endregion
//#region src/schema/prosemirror-json.ts
const ProseMirrorMarkSchema = z.object({
	type: z.string().min(1),
	attrs: z.record(z.string(), z.unknown()).optional()
});
const ProseMirrorNodeSchema = z.lazy(() => z.object({
	type: z.string().min(1),
	attrs: z.record(z.string(), z.unknown()).optional(),
	content: z.array(ProseMirrorNodeSchema).optional(),
	marks: z.array(ProseMirrorMarkSchema).optional(),
	text: z.string().optional()
}));
/**
* How deep a document may nest.
*
* Real documents do not come close: a blockquote holding a nested list five
* levels down sits around 10. The limit exists because the *validator* is
* recursive — Zod's `z.lazy` recursion blows the call stack somewhere in the
* low tens of thousands, which turns a malicious or corrupt file from
* "rejected" into "crashes the tab". Checking depth iteratively first means
* nothing recursive ever sees a tree it cannot handle, and the same guard
* protects the ProseMirror parser and the renderer downstream.
*/
const MAX_DOCUMENT_DEPTH = 100;
/**
* Does the tree under `root` nest deeper than `max`?
*
* Walks defensively over `unknown` — this runs *before* structural validation,
* so it cannot assume anything about the shape it is given.
*/
function exceedsMaxDepth(root, max = 100) {
	const stack = [{
		node: root,
		depth: 0
	}];
	while (stack.length > 0) {
		const entry = stack.pop();
		if (!entry) break;
		if (entry.depth > max) return true;
		const { node, depth } = entry;
		if (typeof node !== "object" || node === null) continue;
		const content = node.content;
		if (!Array.isArray(content)) continue;
		for (const child of content) stack.push({
			node: child,
			depth: depth + 1
		});
	}
	return false;
}
/**
* Walk the tree and collect every node or mark name missing from the registry.
*
* Iterative rather than recursive: a pathological document (or a hostile one)
* can nest thousands of levels deep, and blowing the call stack while
* *validating* untrusted input would turn a rejected document into a crash.
*/
function findUnknownTypes(root) {
	const found = [];
	const stack = [{
		node: root,
		path: ""
	}];
	while (stack.length > 0) {
		const entry = stack.pop();
		if (!entry) break;
		const { node, path } = entry;
		if (!isKnownNodeType(node.type)) found.push({
			kind: "node",
			name: node.type,
			path: path || "root"
		});
		for (const mark of node.marks ?? []) if (!isKnownMarkType(mark.type)) found.push({
			kind: "mark",
			name: mark.type,
			path: path || "root"
		});
		const children = node.content ?? [];
		for (let i = children.length - 1; i >= 0; i -= 1) {
			const child = children[i];
			if (child) stack.push({
				node: child,
				path: path ? `${path}.content.${i}` : `content.${i}`
			});
		}
	}
	return found;
}
//#endregion
//#region src/schema/page-setup-schema.ts
/**
* Zod mirrors of the page-setup types.
*
* These are annotated with the geometry package's own interfaces
* (`z.ZodType<Margins>` and friends) rather than being left to inference, so
* that changing a field in `page-geometry.ts` without updating the validator
* fails to compile. A validator that has silently drifted from the type it
* claims to check is worse than no validator.
*/
const MarginsSchema = z.object({
	topPt: z.number().min(0),
	rightPt: z.number().min(0),
	bottomPt: z.number().min(0),
	leftPt: z.number().min(0)
});
const HeaderFooterSchema = z.object({
	enabled: z.boolean(),
	heightPt: z.number().min(0)
});
const PageSizeSetupSchema = z.discriminatedUnion("kind", [z.object({
	kind: z.literal("preset"),
	id: z.enum(PAGE_SIZE_IDS)
}), z.object({
	kind: z.literal("custom"),
	widthPt: z.number().positive(),
	heightPt: z.number().positive()
})]);
/**
* A colour, checked by the same rule as table cell shading.
*
* These values reach a `style` attribute in an exported HTML file that someone
* opens in a browser, so they are an allowlist rather than any string.
*/
const CssColorSchema = z.string().refine(isSafeCssColor, { message: "not a colour this editor will write into a stylesheet" });
const GradientStopSchema = z.object({
	color: CssColorSchema,
	positionPct: z.number().min(0).max(100)
});
const PageBackgroundSchema = z.discriminatedUnion("kind", [
	z.object({ kind: z.literal("none") }),
	z.object({
		kind: z.literal("color"),
		color: CssColorSchema,
		printed: z.boolean()
	}),
	z.object({
		kind: z.literal("gradient"),
		angleDeg: z.number(),
		stops: z.array(GradientStopSchema).min(2).max(8),
		printed: z.boolean()
	}),
	z.object({
		kind: z.literal("image"),
		src: z.string().startsWith("asset:"),
		fit: z.enum([
			"cover",
			"contain",
			"tile"
		]),
		opacity: z.number().min(0).max(1),
		printed: z.boolean()
	})
]);
const PageSetupSchema = z.object({
	size: PageSizeSetupSchema,
	orientation: z.enum(["portrait", "landscape"]),
	margins: MarginsSchema,
	header: HeaderFooterSchema,
	footer: HeaderFooterSchema,
	background: PageBackgroundSchema.optional(),
	verticalAlign: z.enum([
		"top",
		"middle",
		"bottom"
	]).optional()
});
//#endregion
//#region src/schema/document.ts
/** Bumped whenever the persisted shape changes. See `migrations.ts`. */
const SCHEMA_VERSION = 1;
const DEFAULT_BLOCK_STYLE = {
	fontFamily: "Inter",
	fontSizePt: 11,
	lineHeight: 1.5,
	color: "#202124"
};
const BlockStyleSchema = z.object({
	fontFamily: z.string().min(1),
	fontSizePt: z.number().positive(),
	lineHeight: z.number().positive(),
	color: z.string().regex(/^#[0-9a-fA-F]{6}$/, "Expected a #rrggbb colour")
});
const AssetRefSchema = z.object({
	mime: z.string().min(1),
	width: z.number().positive(),
	height: z.number().positive(),
	byteSize: z.number().min(0)
});
const DocumentMetaSchema = z.object({
	title: z.string(),
	pageSetup: PageSetupSchema,
	defaultStyle: BlockStyleSchema,
	language: z.string().min(1),
	createdAt: z.iso.datetime(),
	updatedAt: z.iso.datetime()
});
const BurfyDocumentSchema = z.object({
	id: z.string().min(1),
	schemaVersion: z.number().int().positive(),
	rev: z.number().int().min(0),
	meta: DocumentMetaSchema,
	content: ProseMirrorNodeSchema,
	assets: z.record(z.string(), AssetRefSchema)
});
var DocumentValidationError = class extends Error {
	issues;
	constructor(message, issues) {
		super(`${message}: ${issues.join("; ")}`);
		this.name = "DocumentValidationError";
		this.issues = issues;
	}
};
/** An empty body — one empty paragraph, because ProseMirror requires a block. */
function createEmptyContent() {
	return {
		type: "doc",
		content: [{ type: "paragraph" }]
	};
}
function createDocument(options = {}) {
	const now = options.now ?? (/* @__PURE__ */ new Date()).toISOString();
	return {
		id: options.id ?? crypto.randomUUID(),
		schemaVersion: 1,
		rev: 0,
		meta: {
			title: options.title ?? "Untitled document",
			pageSetup: options.pageSetup ?? DEFAULT_PAGE_SETUP,
			defaultStyle: DEFAULT_BLOCK_STYLE,
			language: options.language ?? "en",
			createdAt: now,
			updatedAt: now
		},
		content: options.content ?? createEmptyContent(),
		assets: {}
	};
}
/**
* Validate a document that came from storage, a file, or the network.
*
* Both checks run and both are fatal. A structurally valid document
* containing an unknown node type would otherwise load, render as nothing,
* and then be autosaved back — destroying whatever that node held. Refusing
* to open it keeps the file intact and the failure visible.
*/
function parseDocument(raw) {
	const content = raw?.content;
	if (exceedsMaxDepth(content)) throw new DocumentValidationError("Document is nested too deeply", [`content nests deeper than the 100-level limit`]);
	const result = BurfyDocumentSchema.safeParse(raw);
	if (!result.success) throw new DocumentValidationError("Document does not match the expected shape", result.error.issues.map((i) => `${i.path.join(".") || "root"}: ${i.message}`));
	const unknown = findUnknownTypes(result.data.content);
	if (unknown.length > 0) throw new DocumentValidationError("Document contains types this version cannot render", unknown.map((u) => `unknown ${u.kind} "${u.name}" at ${u.path}`));
	return result.data;
}
/** Non-throwing variant for callers that want to report rather than crash. */
function safeParseDocument(raw) {
	try {
		return {
			ok: true,
			document: parseDocument(raw)
		};
	} catch (error) {
		if (error instanceof DocumentValidationError) return {
			ok: false,
			error
		};
		throw error;
	}
}
//#endregion
//#region src/schema/migrations.ts
/** Keyed by the version being migrated *from*. Empty while v1 is current. */
const MIGRATIONS = {};
var DocumentMigrationError = class extends Error {
	constructor(message) {
		super(message);
		this.name = "DocumentMigrationError";
	}
};
function readSchemaVersion(raw) {
	if (typeof raw !== "object" || raw === null) throw new DocumentMigrationError("Document is not an object");
	const version = raw.schemaVersion;
	if (typeof version !== "number" || !Number.isInteger(version) || version < 1) throw new DocumentMigrationError(`Document has no usable schemaVersion (got ${JSON.stringify(version)})`);
	return version;
}
/**
* Run every migration between the document's version and the target.
*
* A document from a *newer* version is refused rather than opened optimistically.
* Opening it would mean silently discarding whatever the newer version added,
* and then autosaving the loss back over the original.
*/
function migrateRaw(raw, migrations = MIGRATIONS, target = 1) {
	let version = readSchemaVersion(raw);
	if (version > target) throw new DocumentMigrationError(`Document was written by a newer version (schemaVersion ${version}, this build supports ${target})`);
	let current = raw;
	while (version < target) {
		const migrate = migrations[version];
		if (!migrate) throw new DocumentMigrationError(`No migration registered from schemaVersion ${version} to ${version + 1}`);
		current = migrate(current);
		version += 1;
		current = {
			...current,
			schemaVersion: version
		};
	}
	return current;
}
/**
* The single entry point for turning stored bytes into a usable document:
* migrate forward, then validate. Every `DocumentStore` implementation should
* call this rather than trusting what it read.
*/
function loadDocument(raw, migrations = MIGRATIONS) {
	return parseDocument(migrateRaw(raw, migrations));
}
//#endregion
//#region src/layout/flow-item.ts
const DEFAULT_ORPHANS = 2;
const DEFAULT_WIDOWS = 2;
//#endregion
//#region src/layout/paginate.ts
const orphansOf = (item) => Math.max(1, item.orphans ?? 2);
const widowsOf = (item) => Math.max(1, item.widows ?? 2);
/**
* How much room the *start* of an item needs before a `keepWithNext`
* predecessor is willing to sit above it.
*/
function leadingRequirementPt(item, contentHeightPt) {
	if (item.kind === "block") return Math.min(item.heightPt, contentHeightPt);
	if (item.kind === "tableRows") {
		const needed = Math.min(Math.max(item.headerRows, 0) + 1, item.rowsPt.length);
		return Math.min(sum(item.rowsPt, 0, needed), contentHeightPt);
	}
	const needed = Math.min(orphansOf(item), item.linesPt.length);
	let total = 0;
	for (let i = 0; i < needed; i += 1) total += item.linesPt[i] ?? 0;
	return Math.min(total, contentHeightPt);
}
/** How many lines from `start` fit in `availablePt`. */
function countFitting(linesPt, start, availablePt) {
	let count = 0;
	let height = 0;
	while (start + count < linesPt.length) {
		const next = linesPt[start + count] ?? 0;
		if (height + next > availablePt) break;
		height += next;
		count += 1;
	}
	return count;
}
function sum(values, from, count) {
	let total = 0;
	for (let i = from; i < from + count; i += 1) total += values[i] ?? 0;
	return total;
}
/**
* Break a measured flow into pages.
*
* A single greedy pass with one item of look-back for `keepWithNext`: no
* backtracking search, no iteration to a fixed point. Determinism matters more
* than optimality here, because the same result has to drive the on-screen
* pages, the print stylesheet and the exported PDF — a layout that could
* settle differently on a second run would break that guarantee.
*/
function paginate({ items, geometry, pageGapPt = 0 }) {
	const contentHeightPt = geometry.contentHeightPt;
	const topChromePt = geometry.contentOriginPt.yPt;
	const betweenPt = geometry.heightPt - (topChromePt + contentHeightPt) + pageGapPt + topChromePt;
	const pageIntervalPt = roundPt(contentHeightPt + betweenPt);
	const breaks = [];
	const pages = [];
	let used = 0;
	let pageIndex = 0;
	/** Close the current page; everything from here starts the next one. */
	function endPage(itemIndex, lineIndex) {
		breaks.push({
			itemIndex,
			lineIndex,
			spacerHeightPt: roundPt(contentHeightPt - used + betweenPt),
			nextPageTopPt: roundPt(topChromePt)
		});
		pages.push({
			index: pageIndex,
			contentTopPt: roundPt(pageIndex * pageIntervalPt),
			usedHeightPt: roundPt(used)
		});
		pageIndex += 1;
		used = 0;
	}
	const remaining = () => contentHeightPt - used;
	/** Place a `lines` item, splitting it across pages as needed. */
	function placeLines(item, itemIndex) {
		const { linesPt } = item;
		const orphans = orphansOf(item);
		const widows = widowsOf(item);
		let start = 0;
		while (start < linesPt.length) {
			let count = countFitting(linesPt, start, remaining());
			if (start + count >= linesPt.length) {
				used += sum(linesPt, start, count);
				return;
			}
			if (linesPt.length - (start + count) < widows) count = Math.max(0, linesPt.length - widows - start);
			if ((count <= 0 || count < orphans) && used > 0) {
				endPage(itemIndex, start === 0 ? null : start);
				continue;
			}
			if (count <= 0) count = 1;
			used += sum(linesPt, start, count);
			start += count;
			if (start < linesPt.length) endPage(itemIndex, start);
		}
	}
	/**
	* Place a table, breaking between rows.
	*
	* When the header repeats, its height is charged to every continuation page
	* before any row is placed there — otherwise the engine would promise room
	* it is about to spend on the repeated header, and the last row on each page
	* would spill past the margin.
	*/
	function placeTableRows(item, itemIndex) {
		const { rowsPt, headerRows, repeatHeader } = item;
		const headerCount = Math.max(0, Math.min(headerRows, rowsPt.length));
		const headerHeight = repeatHeader ? sum(rowsPt, 0, headerCount) : 0;
		let start = 0;
		while (start < rowsPt.length) {
			if (start >= headerCount && start > 0 && headerHeight > 0 && used === 0) used += headerHeight;
			let count = countFitting(rowsPt, start, remaining());
			if (start + count >= rowsPt.length) {
				used += sum(rowsPt, start, count);
				return;
			}
			if (count === 0) {
				if (used > 0) {
					endPage(itemIndex, start === 0 ? null : start);
					continue;
				}
				count = 1;
			}
			used += sum(rowsPt, start, count);
			start += count;
			if (start < rowsPt.length) endPage(itemIndex, start);
		}
	}
	for (let index = 0; index < items.length; index += 1) {
		const item = items[index];
		if (!item) continue;
		if (item.breakBefore && used > 0) endPage(index, null);
		const usedBeforeItem = used;
		const breaksBeforeItem = breaks.length;
		const place = () => {
			if (item.kind === "block") {
				if (used > 0 && item.heightPt > remaining()) endPage(index, null);
				used += item.heightPt;
				return;
			}
			if (item.kind === "tableRows") {
				placeTableRows(item, index);
				return;
			}
			placeLines(item, index);
		};
		place();
		/**
		* `keepWithNext`: if this item sits at the foot of a page with too little
		* room for the start of the next one, move it down as well.
		*
		* Only when the item landed whole on this page and had company above it —
		* pulling down an item that already spans a break achieves nothing, and
		* pulling down the only item on a page would loop.
		*/
		const next = items[index + 1];
		if (item.keepWithNext && next && usedBeforeItem > 0 && breaks.length === breaksBeforeItem && remaining() < leadingRequirementPt(next, contentHeightPt)) {
			used = usedBeforeItem;
			endPage(index, null);
			place();
		}
	}
	pages.push({
		index: pageIndex,
		contentTopPt: roundPt(pageIndex * pageIntervalPt),
		usedHeightPt: roundPt(used)
	});
	return {
		pages,
		breaks,
		pageIntervalPt
	};
}
//#endregion
//#region src/layout/vertical-align.ts
const PAGE_VERTICAL_ALIGNS = [
	"top",
	"middle",
	"bottom"
];
function isPageVerticalAlign(value) {
	return value === "top" || value === "middle" || value === "bottom";
}
/** Fraction of the leftover space that goes above the content. */
const SHARE = {
	top: 0,
	middle: .5,
	bottom: 1
};
/**
* How far down this page's content should start.
*
* Zero for `top`, and zero whenever the page is full — there is nothing to
* distribute, and a page that overflows must not be pushed further out of its
* box in the name of centring it.
*/
function verticalOffsetPt(page, geometry, align = "top") {
	const leftover = geometry.contentHeightPt - page.usedHeightPt;
	if (!Number.isFinite(leftover) || leftover <= 0) return 0;
	return leftover * SHARE[align];
}
//#endregion
//#region src/assets/image-type.ts
/**
* Identifying an image from its bytes.
*
* **The file extension and the browser-reported MIME type are both claims by
* whoever supplied the file.** A `.png` that is really an HTML document, or an
* `image/png` that is really an SVG full of script, is a trivial thing to
* construct. The only trustworthy answer is in the first few bytes, so that is
* what is read.
*/
const IMAGE_TYPES = [
	"png",
	"jpeg",
	"gif",
	"webp",
	"svg"
];
const IMAGE_MIME = {
	png: "image/png",
	jpeg: "image/jpeg",
	gif: "image/gif",
	webp: "image/webp",
	svg: "image/svg+xml"
};
/** Refuse anything larger than this outright, before decoding it. */
const MAX_IMAGE_BYTES = 26214400;
const startsWith = (bytes, signature) => {
	if (bytes.length < signature.length) return false;
	return signature.every((byte, index) => bytes[index] === byte);
};
const PNG = [
	137,
	80,
	78,
	71,
	13,
	10,
	26,
	10
];
const JPEG = [
	255,
	216,
	255
];
const GIF87 = [
	71,
	73,
	70,
	56,
	55,
	97
];
const GIF89 = [
	71,
	73,
	70,
	56,
	57,
	97
];
const RIFF = [
	82,
	73,
	70,
	70
];
const WEBP = [
	87,
	69,
	66,
	80
];
/** Does this look like SVG or XML text? */
function looksLikeSvg(bytes) {
	const head = new TextDecoder("utf-8", { fatal: false }).decode(bytes.subarray(0, 1024)).trim().toLowerCase();
	if (!head.startsWith("<")) return false;
	return head.includes("<svg");
}
/**
* Identify an image, or return `null` for anything unrecognised.
*
* `null` means "do not import this" — not "try harder". Guessing at an
* unidentified byte stream is how a renderer ends up executing one.
*/
function sniffImageType(bytes) {
	if (startsWith(bytes, PNG)) return "png";
	if (startsWith(bytes, JPEG)) return "jpeg";
	if (startsWith(bytes, GIF87) || startsWith(bytes, GIF89)) return "gif";
	if (startsWith(bytes, RIFF) && bytes.length >= 12) {
		const format = bytes.subarray(8, 12);
		if (WEBP.every((byte, index) => format[index] === byte)) return "webp";
	}
	if (looksLikeSvg(bytes)) return "svg";
	return null;
}
/** Decide whether a byte stream may be imported, and as what. */
function checkImage(bytes, maxBytes = MAX_IMAGE_BYTES) {
	if (bytes.length === 0) return {
		ok: false,
		rejection: {
			reason: "empty",
			message: "That file is empty."
		}
	};
	if (bytes.length > maxBytes) return {
		ok: false,
		rejection: {
			reason: "too-large",
			message: `Images must be smaller than ${Math.round(maxBytes / 1048576)} MB.`
		}
	};
	const type = sniffImageType(bytes);
	if (!type) return {
		ok: false,
		rejection: {
			reason: "unsupported-type",
			message: "That file is not a PNG, JPEG, GIF, WebP or SVG image."
		}
	};
	return {
		ok: true,
		type,
		mime: IMAGE_MIME[type]
	};
}
//#endregion
//#region src/assets/font-type.ts
/**
* Identifying a font from its bytes.
*
* Same reasoning as images: the file name and the browser-reported type are
* claims by whoever supplied the file. A font is parsed by the browser's own
* font engine, so handing it something that is not a font is worth avoiding
* on its own merits.
*
* **A user-supplied font URL is never accepted** — only bytes the user
* actually provided. Fetching a font from an address someone typed makes the
* editor an HTTP client for arbitrary destinations.
*/
const FONT_TYPES = [
	"ttf",
	"otf",
	"woff",
	"woff2",
	"ttc"
];
const FONT_MIME = {
	ttf: "font/ttf",
	otf: "font/otf",
	woff: "font/woff",
	woff2: "font/woff2",
	ttc: "font/collection"
};
/** Fonts are small; anything larger is not one. */
const MAX_FONT_BYTES = 10485760;
/** Read a big-endian 32-bit tag. */
function tag(bytes) {
	if (bytes.length < 4) return null;
	return ((bytes[0] ?? 0) << 24 | (bytes[1] ?? 0) << 16 | (bytes[2] ?? 0) << 8 | (bytes[3] ?? 0)) >>> 0;
}
const SFNT_TRUETYPE = 65536;
const SFNT_TRUE = 1953658213;
const OTTO = 1330926671;
const TTCF = 1953784678;
const WOFF = 2001684038;
const WOFF2 = 2001684018;
function sniffFontType(bytes) {
	switch (tag(bytes)) {
		case SFNT_TRUETYPE:
		case SFNT_TRUE: return "ttf";
		case OTTO: return "otf";
		case TTCF: return "ttc";
		case WOFF: return "woff";
		case WOFF2: return "woff2";
		default: return null;
	}
}
function checkFont(bytes, maxBytes = MAX_FONT_BYTES) {
	if (bytes.length === 0) return {
		ok: false,
		rejection: {
			reason: "empty",
			message: "That file is empty."
		}
	};
	if (bytes.length > maxBytes) return {
		ok: false,
		rejection: {
			reason: "too-large",
			message: `Fonts must be smaller than ${Math.round(maxBytes / 1048576)} MB.`
		}
	};
	const type = sniffFontType(bytes);
	if (!type) return {
		ok: false,
		rejection: {
			reason: "unsupported-type",
			message: "That file is not a TTF, OTF, WOFF or WOFF2 font."
		}
	};
	return {
		ok: true,
		type,
		mime: FONT_MIME[type]
	};
}
/**
* A CSS-safe family name derived from a file name.
*
* Uploaded fonts are referenced by family name from inside the document, so
* the name has to survive being written into CSS and into a saved file. Quotes
* and control characters are removed rather than escaped — an escaped quote in
* a family name is a lifetime of subtle bugs for no benefit.
*/
function familyNameFromFileName(fileName) {
	const cleaned = fileName.replace(/\.(ttf|otf|woff2?|ttc)$/i, "").replace(/[\u0000-\u001f"'`\\<>{};()]/g, " ").replace(/[-_]+/g, " ").replace(/\s+/g, " ").trim();
	return cleaned.length > 0 ? cleaned.slice(0, 64) : "Uploaded font";
}
//#endregion
//#region src/ports/asset-store.ts
/**
* Where image bytes live.
*
* Deliberately separate from the document: a document that inlined its images
* could not be loaded, diffed or autosaved cheaply, and every keystroke would
* drag megabytes through the save path. The document holds `asset:<id>`
* references and this holds the bytes.
*/
/** The scheme used to reference an asset from a document. */
const ASSET_URI_SCHEME = "asset:";
function assetUri(id) {
	return `${ASSET_URI_SCHEME}${id}`;
}
/** Extract the id from an `asset:` URI, or `null` if it is not one. */
function parseAssetUri(uri) {
	if (!uri.startsWith("asset:")) return null;
	const id = uri.slice(6);
	return id.length > 0 ? id : null;
}
var AssetNotFoundError = class extends Error {
	id;
	constructor(id) {
		super(`No asset with id "${id}"`);
		this.name = "AssetNotFoundError";
		this.id = id;
	}
};
//#endregion
//#region src/assets/collect-asset-ids.ts
/**
* Every asset a document refers to.
*
* Deliberately a walk over **unvalidated** JSON rather than over a parsed
* `BurfyDocument`. The only caller is the sweep that deletes unreferenced
* image bytes, and a document the validator refuses — one saved by a newer
* version, one with a node type this build does not know — still owns its
* images. Reading references only from documents that parse would delete the
* pictures belonging to every document that does not, which is exactly the
* document you least want to damage further.
*
* So: find `asset:` strings anywhere in the structure, whatever shape it is.
*/
/**
* Depth at which the structure stops being a document and starts being a
* problem.
*
* Generous: a table cell inside a nested list costs a dozen levels or so, and
* the schema's own limit is far below this.
*/
const MAX_DEPTH = 500;
/**
* Raised when the walk cannot finish, rather than returning what it found.
*
* The first version capped the depth and simply stopped, which is worse than
* it sounds: an image nested deeply enough vanished from the results, and the
* sweep then deleted the bytes of a picture a document was still using. A
* partial answer here is indistinguishable from a document having no images,
* so there must not be one.
*/
var AssetScanError = class extends Error {
	constructor(message) {
		super(message);
		this.name = "AssetScanError";
	}
};
function collectAssetIds(value, into = /* @__PURE__ */ new Set()) {
	walk(value, into, 0, /* @__PURE__ */ new WeakSet());
	return into;
}
function walk(value, into, depth, seen) {
	if (depth > MAX_DEPTH) throw new AssetScanError(`document nests deeper than ${MAX_DEPTH} levels`);
	if (typeof value === "string") {
		const id = parseAssetUri(value);
		if (id) into.add(id);
		return;
	}
	if (typeof value !== "object" || value === null) return;
	if (seen.has(value)) return;
	seen.add(value);
	if (Array.isArray(value)) {
		for (const item of value) walk(item, into, depth + 1, seen);
		return;
	}
	for (const key of Object.keys(value)) {
		const child = value[key];
		walk(child, into, depth + 1, seen);
		if (key === "assets" && typeof child === "object" && child !== null) for (const id of Object.keys(child)) into.add(id);
	}
}
//#endregion
//#region src/ports/document-store.ts
var DocumentNotFoundError = class extends Error {
	id;
	constructor(id) {
		super(`No document with id "${id}"`);
		this.name = "DocumentNotFoundError";
		this.id = id;
	}
};
/**
* Raised when a save is based on a stale revision — the document changed
* underneath the caller (another tab, another device).
*/
var DocumentConflictError = class extends Error {
	id;
	expectedRev;
	actualRev;
	constructor(id, expectedRev, actualRev) {
		super(`Document "${id}" was modified elsewhere (expected rev ${expectedRev}, found ${actualRev})`);
		this.name = "DocumentConflictError";
		this.id = id;
		this.expectedRev = expectedRev;
		this.actualRev = actualRev;
	}
};
//#endregion
//#region src/ports/font-provider.ts
function canUploadFonts(provider) {
	return provider !== null && provider !== void 0 && typeof provider.upload === "function";
}
//#endregion
//#region src/export/units.ts
/**
* Unit conversions for the export formats.
*
* Every DOCX bug anyone has ever had is a unit conversion bug, so they live in
* one module with exhaustive tests rather than being inlined at each call
* site. OOXML uses four different units depending on what is being measured,
* and three of them are twentieths, halves or nine-thousandths of something.
*/
/** EMUs per inch — the unit OOXML uses for image and drawing extents. */
const EMU_PER_INCH = 914400;
/** Font sizes are expressed in half-points. */
function ptToHalfPoints(pt) {
	return Math.round(pt * 2);
}
/**
* Points to DXA (twips): twentieths of a point.
*
* Used for margins, indents, spacing and table widths.
*/
function ptToDxa(pt) {
	return Math.round(pt * 20);
}
function dxaToPt(dxa) {
	return dxa / 20;
}
/** Points to EMU, for image extents. */
function ptToEmu(pt) {
	return Math.round(ptToInch(pt) * EMU_PER_INCH);
}
/** CSS pixels (at 96dpi) to EMU. */
function pxToEmu(px) {
	return Math.round(px / 96 * EMU_PER_INCH);
}
/**
* A line-height multiple as OOXML's `line` value.
*
* With `lineRule: "auto"` the value is in 240ths of a line, so single spacing
* is 240 and double is 480 — not a length at all, which is why passing DXA
* here produces the classic "everything is quadruple-spaced" bug.
*/
function lineHeightToTwentiethsOfLine(multiple) {
	return Math.round(multiple * 240);
}
/** Points to whole points, rounded — for the few places OOXML wants them. */
function ptToWholePoints(pt) {
	return Math.round(pt);
}
/** Convert a `#rrggbb` colour to the bare `RRGGBB` OOXML expects. */
function toOoxmlColor(color) {
	return /^#?([0-9a-f]{6})$/i.exec(color.trim())?.[1]?.toUpperCase();
}
/** Sanity: a US Letter page is 8.5in wide. */
const LETTER_WIDTH_PT = 612;
//#endregion
//#region src/export/html.ts
const ESCAPES = {
	"&": "&amp;",
	"<": "&lt;",
	">": "&gt;",
	"\"": "&quot;",
	"'": "&#39;"
};
/**
* Escape text for HTML.
*
* Applied to every value that reaches the output, including attributes — the
* exported file may well be opened in a browser, and a document is untrusted
* content as far as its own export is concerned.
*/
/** The page background, where HTML can express it, as a style fragment. */
function bodyBackground(document) {
	const background = document.meta.pageSetup.background;
	if (!background || background.kind === "image") return "";
	const css = pageBackgroundCss(background);
	return css ? `;background:${escapeHtml(css)}` : "";
}
function escapeHtml(value) {
	return value.replace(/[&<>"']/g, (char) => ESCAPES[char] ?? char);
}
const STYLE_FOR_MARK = {
	[MARK_TYPES.bold]: "font-weight:700",
	[MARK_TYPES.italic]: "font-style:italic",
	[MARK_TYPES.underline]: "text-decoration:underline",
	[MARK_TYPES.strike]: "text-decoration:line-through",
	[MARK_TYPES.subscript]: "vertical-align:sub;font-size:0.8em",
	[MARK_TYPES.superscript]: "vertical-align:super;font-size:0.8em",
	[MARK_TYPES.code]: "font-family:ui-monospace,monospace"
};
function textStyleCss(attrs) {
	if (!attrs) return [];
	const styles = [];
	if (typeof attrs["color"] === "string") styles.push(`color:${attrs["color"]}`);
	if (typeof attrs["backgroundColor"] === "string") styles.push(`background-color:${attrs["backgroundColor"]}`);
	if (typeof attrs["fontFamily"] === "string") styles.push(`font-family:${attrs["fontFamily"]}`);
	if (typeof attrs["fontSize"] === "string") styles.push(`font-size:${attrs["fontSize"]}`);
	return styles;
}
function renderText(node) {
	const text = escapeHtml(node.text ?? "");
	const marks = node.marks ?? [];
	if (marks.length === 0) return text;
	const styles = [];
	let href = null;
	let highlight = null;
	for (const mark of marks) {
		if (mark.type === MARK_TYPES.translitSource) continue;
		if (mark.type === MARK_TYPES.link) {
			const value = mark.attrs?.["href"];
			if (typeof value === "string") href = value;
			continue;
		}
		if (mark.type === MARK_TYPES.highlight) {
			const color = mark.attrs?.["color"];
			highlight = isSafeCssColor(color) ? color : DEFAULT_HIGHLIGHT_COLOR;
			continue;
		}
		if (mark.type === MARK_TYPES.textStyle) {
			styles.push(...textStyleCss(mark.attrs));
			continue;
		}
		const style = STYLE_FOR_MARK[mark.type];
		if (style) styles.push(style);
	}
	let html = text;
	if (highlight) html = `<mark style="background-color:${escapeHtml(highlight)}">${html}</mark>`;
	if (styles.length > 0) html = `<span style="${escapeHtml(styles.join(";"))}">${html}</span>`;
	if (href) html = `<a href="${escapeHtml(href)}" rel="noopener noreferrer">${html}</a>`;
	return html;
}
function blockStyle(attrs) {
	if (!attrs) return "";
	const styles = [];
	if (typeof attrs["textAlign"] === "string") styles.push(`text-align:${attrs["textAlign"]}`);
	if (typeof attrs["lineHeight"] === "number") styles.push(`line-height:${attrs["lineHeight"]}`);
	if (typeof attrs["spaceBeforePt"] === "number") styles.push(`margin-top:${attrs["spaceBeforePt"]}pt`);
	if (typeof attrs["spaceAfterPt"] === "number") styles.push(`margin-bottom:${attrs["spaceAfterPt"]}pt`);
	if (typeof attrs["indentPt"] === "number") styles.push(`margin-left:${attrs["indentPt"]}pt`);
	return styles.length > 0 ? ` style="${escapeHtml(styles.join(";"))}"` : "";
}
function renderChildren(node, options) {
	return (node.content ?? []).map((child) => renderNode(child, options)).join("");
}
function renderImage(node, options) {
	const src = typeof node.attrs?.["src"] === "string" ? node.attrs["src"] : "";
	const assetId = src.startsWith("asset:") ? src.slice(6) : null;
	const resolved = assetId ? options.resolveImage?.(assetId) ?? null : src;
	if (!resolved) return "";
	const alt = typeof node.attrs?.["alt"] === "string" ? node.attrs["alt"] : "";
	const width = typeof node.attrs?.["widthPx"] === "number" ? node.attrs["widthPx"] : null;
	const layout = node.attrs?.["layout"];
	const styles = [];
	if (layout === "wrapLeft") styles.push("float:left", "margin:0 12px 8px 0");
	if (layout === "wrapRight") styles.push("float:right", "margin:0 0 8px 12px");
	return [
		"<img",
		` src="${escapeHtml(resolved)}"`,
		` alt="${escapeHtml(alt)}"`,
		width === null ? "" : ` width="${Math.round(width)}"`,
		styles.length > 0 ? ` style="${escapeHtml(styles.join(";"))}"` : "",
		" />"
	].join("");
}
function renderNode(node, options) {
	switch (node.type) {
		case NODE_TYPES.text: return renderText(node);
		case NODE_TYPES.paragraph: {
			const inner = renderChildren(node, options);
			return `<p${blockStyle(node.attrs)}>${inner || "<br />"}</p>`;
		}
		case NODE_TYPES.heading: {
			const level = typeof node.attrs?.["level"] === "number" ? node.attrs["level"] : 1;
			const tag = `h${Math.min(6, Math.max(1, level))}`;
			return `<${tag}${blockStyle(node.attrs)}>${renderChildren(node, options)}</${tag}>`;
		}
		case NODE_TYPES.blockquote: return `<blockquote>${renderChildren(node, options)}</blockquote>`;
		case NODE_TYPES.codeBlock: return `<pre><code>${escapeHtml(node.content?.[0]?.text ?? "")}</code></pre>`;
		case NODE_TYPES.horizontalRule: return "<hr />";
		case NODE_TYPES.hardBreak: return "<br />";
		case NODE_TYPES.pageBreak: return "<div style=\"break-after:page\"></div>";
		case NODE_TYPES.bulletList:
		case NODE_TYPES.taskList: return `<ul>${renderChildren(node, options)}</ul>`;
		case NODE_TYPES.orderedList: return `<ol>${renderChildren(node, options)}</ol>`;
		case NODE_TYPES.listItem: return `<li>${renderChildren(node, options)}</li>`;
		case NODE_TYPES.taskItem: return `<li>${node.attrs?.["checked"] === true ? "☑" : "☐"} ${renderChildren(node, options)}</li>`;
		case NODE_TYPES.table: return `<table border="1" style="border-collapse:collapse">${renderChildren(node, options)}</table>`;
		case NODE_TYPES.tableRow: return `<tr>${renderChildren(node, options)}</tr>`;
		case NODE_TYPES.tableCell:
		case NODE_TYPES.tableHeader: {
			const tag = node.type === NODE_TYPES.tableHeader ? "th" : "td";
			const colspan = node.attrs?.["colspan"];
			const rowspan = node.attrs?.["rowspan"];
			const style = cellStyle(readCellProperties(node.attrs));
			return `<${tag}${[
				typeof colspan === "number" && colspan > 1 ? ` colspan="${colspan}"` : "",
				typeof rowspan === "number" && rowspan > 1 ? ` rowspan="${rowspan}"` : "",
				style ? ` style="${escapeHtml(style)}"` : ""
			].join("")}>${renderChildren(node, options)}</${tag}>`;
		}
		case NODE_TYPES.image: return renderImage(node, options);
		default: return renderChildren(node, options);
	}
}
function toHtml(document, options = {}) {
	const body = renderChildren(document.content, options);
	if (!options.standalone) return body;
	const style = document.meta.defaultStyle;
	return [
		"<!doctype html>",
		"<html><head><meta charset=\"utf-8\" />",
		`<title>${escapeHtml(document.meta.title)}</title>`,
		"</head>",
		`<body style="font-family:${escapeHtml(style.fontFamily)};font-size:${style.fontSizePt}pt;line-height:${style.lineHeight};color:${escapeHtml(style.color)}${bodyBackground(document)}">`,
		body,
		"</body></html>"
	].join("\n");
}
//#endregion
//#region src/export/markdown.ts
/**
* Export to GitHub-flavoured Markdown, and to plain text.
*
* Markdown cannot express text wrap, colours, fonts or page setup, and
* pretending otherwise would produce a file that looks like the document but
* is not. Those are dropped, and the export dialog says so — an honest lossy
* format beats a dishonest lossless-looking one.
*/
/** Escape the characters that would otherwise become Markdown syntax. */
function escapeMarkdown(text) {
	return text.replace(/([\\`*_{}[\]()#+\-.!|>])/g, "\\$1");
}
function renderInline(node) {
	if (node.type === NODE_TYPES.hardBreak) return "  \n";
	if (node.type !== NODE_TYPES.text) return renderChildrenInline(node);
	let text = escapeMarkdown(node.text ?? "");
	let href = null;
	for (const mark of node.marks ?? []) switch (mark.type) {
		case MARK_TYPES.bold:
			text = `**${text}**`;
			break;
		case MARK_TYPES.italic:
			text = `_${text}_`;
			break;
		case MARK_TYPES.strike:
			text = `~~${text}~~`;
			break;
		case MARK_TYPES.code:
			text = `\`${node.text ?? ""}\``;
			break;
		case MARK_TYPES.link: {
			const value = mark.attrs?.["href"];
			if (typeof value === "string") href = value;
			break;
		}
	}
	return href ? `[${text}](${href})` : text;
}
function renderChildrenInline(node) {
	return (node.content ?? []).map(renderInline).join("");
}
function renderList(node, depth, ordered) {
	const indent = "  ".repeat(depth);
	return (node.content ?? []).map((item, index) => {
		const checked = item.attrs?.["checked"];
		const marker = ordered ? `${index + 1}.` : typeof checked === "boolean" ? `- [${checked ? "x" : " "}]` : "-";
		const body = (item.content ?? []).map((child) => child.type === NODE_TYPES.bulletList || child.type === NODE_TYPES.orderedList || child.type === NODE_TYPES.taskList ? `\n${renderList(child, depth + 1, child.type === NODE_TYPES.orderedList)}` : renderChildrenInline(child)).join("");
		return `${indent}${marker} ${body}`;
	}).join("\n");
}
function renderTable(node) {
	const rows = (node.content ?? []).map((row) => (row.content ?? []).map((cell) => renderChildrenInline(cell)));
	if (rows.length === 0) return "";
	const header = rows[0] ?? [];
	const separator = header.map(() => "---");
	const body = rows.slice(1);
	return [
		`| ${header.join(" | ")} |`,
		`| ${separator.join(" | ")} |`,
		...body.map((row) => `| ${row.join(" | ")} |`)
	].join("\n");
}
function renderBlock(node) {
	switch (node.type) {
		case NODE_TYPES.paragraph: return renderChildrenInline(node);
		case NODE_TYPES.heading: {
			const level = typeof node.attrs?.["level"] === "number" ? node.attrs["level"] : 1;
			return `${"#".repeat(Math.min(6, Math.max(1, level)))} ${renderChildrenInline(node)}`;
		}
		case NODE_TYPES.blockquote: return (node.content ?? []).map((child) => `> ${renderBlock(child)}`).join("\n>\n");
		case NODE_TYPES.codeBlock: {
			const language = node.attrs?.["language"];
			return `${typeof language === "string" ? `\`\`\`${language}` : "```"}\n${node.content?.[0]?.text ?? ""}\n\`\`\``;
		}
		case NODE_TYPES.horizontalRule: return "---";
		case NODE_TYPES.pageBreak: return "---";
		case NODE_TYPES.bulletList:
		case NODE_TYPES.taskList: return renderList(node, 0, false);
		case NODE_TYPES.orderedList: return renderList(node, 0, true);
		case NODE_TYPES.table: return renderTable(node);
		case NODE_TYPES.image: {
			const alt = typeof node.attrs?.["alt"] === "string" ? node.attrs["alt"] : "";
			const src = typeof node.attrs?.["src"] === "string" ? node.attrs["src"] : "";
			return `![${escapeMarkdown(alt)}](${src})`;
		}
		default: return renderChildrenInline(node);
	}
}
function toMarkdown(document) {
	return `${(document.content.content ?? []).map(renderBlock).filter((block) => block !== "").join("\n\n")}\n`;
}
/** Flow order, list markers kept, tables as tab-separated rows. */
function toPlainText(document) {
	const renderPlain = (node) => {
		if (node.type === NODE_TYPES.text) return node.text ?? "";
		if (node.type === NODE_TYPES.hardBreak) return "\n";
		if (node.type === NODE_TYPES.tableRow) return (node.content ?? []).map(renderPlain).join("	");
		const children = (node.content ?? []).map(renderPlain);
		const isBlock = node.type !== NODE_TYPES.tableCell && node.type !== NODE_TYPES.tableHeader;
		return children.join(isBlock ? "" : "");
	};
	return `${(document.content.content ?? []).map((block) => {
		if (block.type === NODE_TYPES.table) return (block.content ?? []).map(renderPlain).join("\n");
		if (block.type === NODE_TYPES.bulletList || block.type === NODE_TYPES.orderedList || block.type === NODE_TYPES.taskList) return (block.content ?? []).map((item, index) => block.type === NODE_TYPES.orderedList ? `${index + 1}. ${renderPlain(item)}` : `- ${renderPlain(item)}`).join("\n");
		return renderPlain(block);
	}).join("\n\n")}\n`;
}
//#endregion
//#region src/export/docx.ts
const NUMBERING_REFERENCE = "burfy-ordered";
const ALIGNMENT = {
	left: AlignmentType.LEFT,
	center: AlignmentType.CENTER,
	right: AlignmentType.RIGHT,
	justify: AlignmentType.JUSTIFIED
};
const HEADING = {
	1: HeadingLevel.HEADING_1,
	2: HeadingLevel.HEADING_2,
	3: HeadingLevel.HEADING_3,
	4: HeadingLevel.HEADING_4,
	5: HeadingLevel.HEADING_5,
	6: HeadingLevel.HEADING_6
};
/** Turn one text node into a run, carrying its marks. */
function toRun(node) {
	const options = { text: node.text ?? "" };
	for (const mark of node.marks ?? []) switch (mark.type) {
		case MARK_TYPES.bold:
			options.bold = true;
			break;
		case MARK_TYPES.italic:
			options.italics = true;
			break;
		case MARK_TYPES.underline:
			options.underline = {};
			break;
		case MARK_TYPES.strike:
			options.strike = true;
			break;
		case MARK_TYPES.subscript:
			options.subScript = true;
			break;
		case MARK_TYPES.superscript:
			options.superScript = true;
			break;
		case MARK_TYPES.highlight: {
			const fill = toOoxmlColor(toHexColor(typeof mark.attrs?.["color"] === "string" ? mark.attrs["color"] : "") ?? "") ?? toOoxmlColor("#fff475") ?? "FFF475";
			options.shading = {
				type: ShadingType.CLEAR,
				fill
			};
			break;
		}
		case MARK_TYPES.code:
			options.font = "Consolas";
			break;
		case MARK_TYPES.textStyle: {
			const family = mark.attrs?.["fontFamily"];
			if (typeof family === "string") options.font = family;
			const size = mark.attrs?.["fontSize"];
			if (typeof size === "string") {
				const points = Number.parseFloat(size);
				if (Number.isFinite(points)) options.size = ptToHalfPoints(points);
			}
			const color = mark.attrs?.["color"];
			if (typeof color === "string") {
				const ooxml = toOoxmlColor(color);
				if (ooxml) options.color = ooxml;
			}
			break;
		}
	}
	return new TextRun(options);
}
function runsOf(node, options) {
	const runs = [];
	for (const child of node.content ?? []) {
		if (child.type === NODE_TYPES.text) {
			runs.push(toRun(child));
			continue;
		}
		if (child.type === NODE_TYPES.hardBreak) {
			runs.push(new TextRun({
				text: "",
				break: 1
			}));
			continue;
		}
		if (child.type === NODE_TYPES.image) {
			const image = toImageRun(child, options);
			if (image) runs.push(image);
			continue;
		}
		runs.push(...runsOf(child, options));
	}
	return runs;
}
function toImageRun(node, options) {
	const src = typeof node.attrs?.["src"] === "string" ? node.attrs["src"] : "";
	const assetId = src.startsWith("asset:") ? src.slice(6) : null;
	if (!assetId) return null;
	const bytes = options.resolveImage?.(assetId);
	if (!bytes) return null;
	const width = typeof node.attrs?.["widthPx"] === "number" ? node.attrs["widthPx"] : 200;
	const height = typeof node.attrs?.["heightPx"] === "number" ? node.attrs["heightPx"] : 200;
	return new ImageRun({
		type: "png",
		data: bytes,
		transformation: {
			width: Math.round(width),
			height: Math.round(height)
		}
	});
}
/** Paragraph-level properties, shared by paragraphs and headings. */
function paragraphOptions(node) {
	const attrs = node.attrs ?? {};
	const options = {};
	const align = attrs["textAlign"];
	const alignment = typeof align === "string" ? ALIGNMENT[align] : void 0;
	if (alignment) options.alignment = alignment;
	const spacing = {};
	if (typeof attrs["spaceBeforePt"] === "number") spacing.before = ptToDxa(attrs["spaceBeforePt"]);
	if (typeof attrs["spaceAfterPt"] === "number") spacing.after = ptToDxa(attrs["spaceAfterPt"]);
	if (typeof attrs["lineHeight"] === "number") {
		spacing.line = lineHeightToTwentiethsOfLine(attrs["lineHeight"]);
		spacing.lineRule = "auto";
	}
	if (Object.keys(spacing).length > 0) options.spacing = spacing;
	if (typeof attrs["indentPt"] === "number") options.indent = { left: ptToDxa(attrs["indentPt"]) };
	return options;
}
function toParagraphs(node, options) {
	switch (node.type) {
		case NODE_TYPES.paragraph: return [new Paragraph({
			...paragraphOptions(node),
			children: runsOf(node, options)
		})];
		case NODE_TYPES.heading: {
			const level = typeof node.attrs?.["level"] === "number" ? node.attrs["level"] : 1;
			const heading = HEADING[Math.min(6, Math.max(1, level))];
			return [new Paragraph({
				...paragraphOptions(node),
				...heading ? { heading } : {},
				children: runsOf(node, options)
			})];
		}
		case NODE_TYPES.blockquote: return (node.content ?? []).flatMap((child) => toParagraphs(child, options).map((paragraph) => paragraph));
		case NODE_TYPES.codeBlock: return [new Paragraph({ children: [new TextRun({
			text: node.content?.[0]?.text ?? "",
			font: "Consolas"
		})] })];
		case NODE_TYPES.horizontalRule: return [new Paragraph({
			thematicBreak: true,
			children: []
		})];
		case NODE_TYPES.pageBreak: return [new Paragraph({ children: [new PageBreak()] })];
		case NODE_TYPES.bulletList:
		case NODE_TYPES.taskList:
		case NODE_TYPES.orderedList: return listParagraphs(node, options, 0);
		default: return (node.content ?? []).flatMap((child) => toParagraphs(child, options));
	}
}
function listParagraphs(list, options, level) {
	const ordered = list.type === NODE_TYPES.orderedList;
	const paragraphs = [];
	for (const item of list.content ?? []) for (const child of item.content ?? []) {
		if (child.type === NODE_TYPES.bulletList || child.type === NODE_TYPES.orderedList || child.type === NODE_TYPES.taskList) {
			paragraphs.push(...listParagraphs(child, options, level + 1));
			continue;
		}
		paragraphs.push(new Paragraph({
			...paragraphOptions(child),
			children: runsOf(child, options),
			...ordered ? { numbering: {
				reference: NUMBERING_REFERENCE,
				level
			} } : { bullet: { level } }
		}));
	}
	return paragraphs;
}
/**
* CSS pixels to eighths of a point, which is how OOXML sizes a border.
*
* A 1px border is 0.75pt is 6 eighths. Word clamps this to 2..96, and a border
* asked for and not drawn is worse than one drawn a shade thicker.
*/
function pxToBorderSize(px) {
	return Math.min(96, Math.max(2, Math.round(px * 6)));
}
/** CSS pixels to DXA (twips): 96 pixels and 1440 twips both make an inch. */
function pxToDxa(px) {
	return Math.round(px * 15);
}
const DOCX_VERTICAL_ALIGN = {
	top: VerticalAlignTable.TOP,
	middle: VerticalAlignTable.CENTER,
	bottom: VerticalAlignTable.BOTTOM
};
/**
* Cell formatting, mapped to what Word understands.
*
* A cell shaded grey on screen and white in Word is a disagreement that only
* shows up in someone else's printout, so the properties are read through the
* same function the editor and the HTML exporter use.
*
* Word takes shading as a bare six-digit hex. `rgb()` — which is what the DOM
* turns every colour into on an HTML round trip — is converted; a bare name
* cannot be, and is left unshaded rather than guessed at.
*/
function cellFormatting(properties) {
	const formatting = {};
	const fill = toOoxmlColor(toHexColor(properties.backgroundColor) ?? "");
	if (fill) formatting["shading"] = {
		type: ShadingType.CLEAR,
		fill
	};
	if (properties.verticalAlign) formatting["verticalAlign"] = DOCX_VERTICAL_ALIGN[properties.verticalAlign];
	if (properties.paddingPx !== null) {
		const margin = pxToDxa(properties.paddingPx);
		formatting["margins"] = {
			top: margin,
			bottom: margin,
			left: margin,
			right: margin
		};
	}
	if (properties.borderWidthPx !== null || properties.borderColor) {
		const width = properties.borderWidthPx ?? 1;
		const color = properties.borderColor ? toOoxmlColor(properties.borderColor) : void 0;
		const side = width === 0 ? {
			style: BorderStyle.NONE,
			size: 0,
			color: "auto"
		} : {
			style: BorderStyle.SINGLE,
			size: pxToBorderSize(width),
			color: color ?? "auto"
		};
		formatting["borders"] = {
			top: side,
			bottom: side,
			left: side,
			right: side
		};
	}
	return formatting;
}
function toTable(node, options) {
	const rows = (node.content ?? []).map((row) => {
		const cells = (row.content ?? []).map((cell) => {
			const colspan = cell.attrs?.["colspan"];
			const rowspan = cell.attrs?.["rowspan"];
			return new TableCell({
				children: (cell.content ?? []).flatMap((child) => toParagraphs(child, options)),
				...typeof colspan === "number" && colspan > 1 ? { columnSpan: colspan } : {},
				...typeof rowspan === "number" && rowspan > 1 ? { rowSpan: rowspan } : {},
				...cellFormatting(readCellProperties(cell.attrs))
			});
		});
		const isHeader = (row.content ?? []).some((cell) => cell.type === NODE_TYPES.tableHeader);
		return new TableRow({
			children: cells,
			...isHeader ? { tableHeader: true } : {}
		});
	});
	return new Table({
		rows,
		width: {
			size: 100,
			type: WidthType.PERCENTAGE
		}
	});
}
/** Blocks, in order, as the body of the section. */
function bodyOf(document, options) {
	return (document.content.content ?? []).flatMap((block) => block.type === NODE_TYPES.table ? [toTable(block, options)] : toParagraphs(block, options));
}
function buildDocx(document, options = {}) {
	const geometry = createPageGeometry(document.meta.pageSetup);
	const style = document.meta.defaultStyle;
	const defaultColor = toOoxmlColor(style.color);
	const pageBackground = toOoxmlColor(flatBackgroundColor(document.meta.pageSetup.background ?? { kind: "none" }) ?? "");
	return new Document({
		...pageBackground ? { background: { color: pageBackground } } : {},
		styles: { default: { document: {
			run: {
				font: style.fontFamily,
				size: ptToHalfPoints(style.fontSizePt),
				...defaultColor ? { color: defaultColor } : {}
			},
			paragraph: { spacing: {
				line: lineHeightToTwentiethsOfLine(style.lineHeight),
				lineRule: "auto"
			} }
		} } },
		numbering: { config: [{
			reference: NUMBERING_REFERENCE,
			levels: Array.from({ length: 5 }, (_, level) => ({
				level,
				format: LevelFormat.DECIMAL,
				text: `%${level + 1}.`,
				alignment: AlignmentType.START
			}))
		}] },
		sections: [{
			properties: { page: {
				size: {
					width: ptToDxa(geometry.widthPt),
					height: ptToDxa(geometry.heightPt)
				},
				margin: {
					top: ptToDxa(geometry.margins.topPt),
					right: ptToDxa(geometry.margins.rightPt),
					bottom: ptToDxa(geometry.margins.bottomPt),
					left: ptToDxa(geometry.margins.leftPt)
				}
			} },
			children: bodyOf(document, options)
		}]
	});
}
/**
* Build the document and pack it into bytes.
*
* Via `toBlob`, not `toBuffer`: the latter is Node-only and silently fails in
* a browser, which is where this actually runs. `Blob` exists in browsers,
* workers and Node 18+, so one path serves every environment.
*/
async function toDocx(document, options = {}) {
	const blob = await Packer.toBlob(buildDocx(document, options));
	return new Uint8Array(await blob.arrayBuffer());
}
/** Exposed so a test can assert the EMU conversion without unzipping a file. */
const imageExtentEmu = (widthPt, heightPt) => ({
	width: ptToEmu(widthPt),
	height: ptToEmu(heightPt)
});
//#endregion
//#region src/search/find-matches.ts
/**
* Is the character at `index` part of a word?
*
* Unicode properties rather than `\w`, which is ASCII-only: with `\w`, every
* Malayalam character counts as a boundary and "whole word" matches nothing.
*
* `\p{M}` is as important as `\p{L}`. Indic scripts attach combining marks to
* their consonants, so a word can *end* in one — without marks counted as word
* characters, "ലോകം" is found as a whole word inside "നമസ്കാരംലോകം", because
* the anusvara before it looks like a boundary.
*/
const WORD_CHARACTER = /[\p{L}\p{N}\p{M}_]/u;
function isWordCharacter(text, index) {
	const char = text[index];
	return char !== void 0 && WORD_CHARACTER.test(char);
}
/**
* Every occurrence of `query` in `haystack`, in order.
*
* Overlapping matches are not reported: after a hit the scan resumes at its
* end, so searching "aa" in "aaa" finds one match, which is what a person
* stepping through results expects.
*/
function findMatches(haystack, query, options = {}) {
	if (!query) return [];
	const { caseSensitive = false, wholeWord = false } = options;
	const subject = caseSensitive ? haystack : haystack.toLowerCase();
	const needle = caseSensitive ? query : query.toLowerCase();
	const matches = [];
	let from = 0;
	while (from <= subject.length - needle.length) {
		const start = subject.indexOf(needle, from);
		if (start === -1) break;
		const end = start + needle.length;
		if (wholeWord) {
			const boundedBefore = start === 0 || !isWordCharacter(haystack, start - 1);
			const boundedAfter = end === haystack.length || !isWordCharacter(haystack, end);
			if (!boundedBefore || !boundedAfter) {
				from = start + 1;
				continue;
			}
		}
		matches.push({
			start,
			end
		});
		from = end;
	}
	return matches;
}
/**
* Index of the first match at or after `position`, wrapping to the start.
*
* Returns `-1` only when there are no matches at all — searching should always
* take you somewhere if there is anywhere to go.
*/
function nextMatchIndex(matches, position) {
	if (matches.length === 0) return -1;
	const found = matches.findIndex((match) => match.start >= position);
	return found === -1 ? 0 : found;
}
/** Index of the last match at or before `position`, wrapping to the end. */
function previousMatchIndex(matches, position) {
	if (matches.length === 0) return -1;
	for (let index = matches.length - 1; index >= 0; index -= 1) {
		const match = matches[index];
		if (match && match.end <= position) return index;
	}
	return matches.length - 1;
}
//#endregion
//#region src/stats/text-stats.ts
/**
* A word is a run of letters, numbers or marks.
*
* `\p{M}` matters: Indic scripts attach combining marks to their consonants,
* and a pattern without it splits "നമസ്കാരം" into several "words". `\w` would
* be worse still — being ASCII-only, it counts the whole thing as zero.
*/
const WORD = /[\p{L}\p{N}][\p{L}\p{N}\p{M}'’-]*/gu;
function countWords(text) {
	return text.match(WORD)?.length ?? 0;
}
/** Concatenate a node's text, separating block-level content with newlines. */
function collectText(node) {
	if (node.type === NODE_TYPES.text) return node.text ?? "";
	if (node.type === NODE_TYPES.hardBreak) return "\n";
	const children = (node.content ?? []).map(collectText);
	return node.type === NODE_TYPES.text || node.type === NODE_TYPES.image ? children.join("") : children.join("\n");
}
function countText(text) {
	return {
		words: countWords(text),
		characters: text.length,
		charactersNoSpaces: text.replace(/\s/gu, "").length
	};
}
function documentStats(document) {
	const text = collectText(document.content);
	let paragraphs = 0;
	const visit = (node) => {
		if (node.type === NODE_TYPES.paragraph && collectText(node).trim() !== "") paragraphs += 1;
		for (const child of node.content ?? []) visit(child);
	};
	visit(document.content);
	return {
		...countText(text),
		paragraphs
	};
}
/**
* The document's headings, in order.
*
* Only top-level headings: one inside a table cell or a list is a label for
* that thing, not a section of the document, and putting it in the outline
* makes the outline useless.
*/
function documentOutline(document) {
	const outline = [];
	(document.content.content ?? []).forEach((block, blockIndex) => {
		if (block.type !== NODE_TYPES.heading) return;
		const level = typeof block.attrs?.["level"] === "number" ? block.attrs["level"] : 1;
		const text = collectText(block).trim();
		if (text === "") return;
		outline.push({
			level,
			text,
			blockIndex
		});
	});
	return outline;
}
//#endregion
export { ASSET_URI_SCHEME, AssetNotFoundError, AssetRefSchema, AssetScanError, BLOCK_ATTRS, BlockStyleSchema, BurfyDocumentSchema, CELL_PROPERTY_ATTRIBUTES, CELL_VERTICAL_ALIGNS, CSS_PX_PER_INCH, DEFAULT_BLOCK_STYLE, DEFAULT_FOOTER, DEFAULT_HEADER, DEFAULT_HIGHLIGHT_COLOR, DEFAULT_MARGINS, DEFAULT_ORPHANS, DEFAULT_PAGE_SETUP, DEFAULT_WIDOWS, DocumentConflictError, DocumentMetaSchema, DocumentMigrationError, DocumentNotFoundError, DocumentValidationError, EMPTY_CELL_PROPERTIES, EMU_PER_INCH, FONT_MIME, FONT_TYPES, HEADING_LEVELS, HeaderFooterSchema, IMAGE_LAYOUTS, IMAGE_MIME, IMAGE_TYPES, INDENT_STEP_PT, InvalidPageSetupError, LETTER_WIDTH_PT, LINE_HEIGHT_PRESETS, MARK_TYPES, MAX_BORDER_WIDTH_PX, MAX_DOCUMENT_DEPTH, MAX_FONT_BYTES, MAX_IMAGE_BYTES, MAX_INDENT_PT, MAX_PADDING_PX, MIGRATIONS, MIN_CONTENT_WIDTH_PT, MM_PER_INCH, MarginsSchema, NODE_TYPES, NO_PAGE_BACKGROUND, PAGE_SIZES, PAGE_SIZE_IDS, PAGE_VERTICAL_ALIGNS, PT_PER_INCH, PageBackgroundSchema, PageSetupSchema, PageSizeSetupSchema, ProseMirrorMarkSchema, ProseMirrorNodeSchema, SCHEMA_VERSION, TEXT_ALIGNMENTS, UNIT_LENGTH_PT, assetUri, backgroundIsDark, buildDocx, canUploadFonts, cellStyle, cellStyleDeclarations, checkFont, checkImage, clampIndent, clampMargin, collectAssetIds, collectText, countText, countWords, createDocument, createEmptyContent, createPageGeometry, cssPxToPt, documentOutline, documentStats, dxaToPt, escapeHtml, escapeMarkdown, exceedsMaxDepth, familyNameFromFileName, findMatches, findUnknownTypes, flatBackgroundColor, hasCellProperties, imageExtentEmu, inchToPt, isImageLayout, isKnownMarkType, isKnownNodeType, isPageSizeId, isPageVerticalAlign, isPrinted, isSafeCssColor, lineHeightToTwentiethsOfLine, loadDocument, migrateRaw, mmToPt, nextMatchIndex, orient, pageBackgroundCss, paginate, parseAssetUri, parseDocument, previousMatchIndex, ptToCssPx, ptToDxa, ptToEmu, ptToHalfPoints, ptToInch, ptToMm, ptToWholePoints, pxToEmu, readCellProperties, resolvePageSize, roundPt, rulerTicks, safeParseDocument, snapIncrementPt, snapToIncrement, sniffFontType, sniffImageType, toDocx, toHexColor, toHtml, toMarkdown, toOoxmlColor, toPlainText, verticalOffsetPt };
